/**
 * Consolidated Firebase Authentication Service
 * Combines user authentication, management, and utilities
 */

import { 
  getAuth, 
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  signInWithEmailLink, 
  isSignInWithEmailLink,
  type ActionCodeSettings,
  type User as FirebaseUser
} from 'firebase/auth';
import type { User, UserRoleType } from '@/lib/types';
import { isSampleDataMode } from '@/lib/config/env';
import { convertFirebaseUser, convertAdminUser } from './core';
import { mapFirebaseError, type AuthError as MappedAuthError } from '@/lib/error-handling';

// =============================================================================
// UNIFIED AUTHENTICATION TYPES
// =============================================================================

export type AuthMethod = 'password' | 'magic-link';

export interface AuthCredentials {
  email: string;
  password?: string; // Optional for magic link method
}

export interface AuthOptions {
  method: AuthMethod;
  rememberMe?: boolean;
  redirectUrl?: string;
}

export interface AuthResult {
  success: boolean;
  user?: FirebaseUser;
  error?: MappedAuthError;
  requiresVerification?: boolean; // For magic link method
}

export interface MagicLinkStatus {
  email: string;
  sentAt: string;
  expiresAt: string;
}

// =============================================================================
// AUTHENTICATION SERVICE
// =============================================================================

export class FirebaseAuthService {
  private static instance: FirebaseAuthService;

  static getInstance(): FirebaseAuthService {
    if (!this.instance) {
      this.instance = new FirebaseAuthService();
    }
    return this.instance;
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;
    
    if (!firebaseUser) {
      return null;
    }

    return convertFirebaseUser(firebaseUser);
  }

  /**
   * Get user by ID - handles both client and server side
   */
  async getUserById(uid: string): Promise<User | null> {
    if (typeof window !== 'undefined') {
      // Client-side: only return current user if it matches
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      
      if (firebaseUser && firebaseUser.uid === uid) {
        return convertFirebaseUser(firebaseUser);
      }
      return null;
    }

    // Server-side: check sample data mode first
    if (isSampleDataMode()) {
      const { getSampleUserById } = await import('@/lib/sample-data/users');
      return getSampleUserById(uid);
    }

    // Server-side: use Firebase Admin SDK
    try {
      const { adminAuth } = await import('./admin');
      const adminAuthInstance = await adminAuth();
      
      const userRecord = await adminAuthInstance.getUser(uid);
      return convertAdminUser(userRecord);
    } catch (error) {
      console.error('Error getting user by ID:', error);
      return null;
    }
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email: string): Promise<User | null> {
    if (typeof window !== 'undefined') {
      // Client-side: check current user and wait for auth state if needed
      const auth = getAuth();
      
      // If we have a current user and email matches, return it
      if (auth.currentUser && auth.currentUser.email === email) {
        return convertFirebaseUser(auth.currentUser);
      }
      
      // If no current user but auth is available, wait briefly for auth state to settle
      if (auth && !auth.currentUser) {
        try {
          // Wait for auth state to settle (up to 2 seconds)
          const user = await new Promise<FirebaseUser | null>((resolve) => {
            const timeout = setTimeout(() => resolve(null), 2000);
            const unsubscribe = auth.onAuthStateChanged((user) => {
              clearTimeout(timeout);
              unsubscribe();
              resolve(user);
            });
          });
          
          if (user && user.email === email) {
            return convertFirebaseUser(user);
          }
        } catch (error) {
          console.warn('Error waiting for auth state:', error);
        }
      }
      
      return null;
    }

    // Server-side: Always use Firebase Admin SDK (sample users are now in Firebase Auth)
    try {
      const { adminAuth } = await import('./admin');
      const adminAuthInstance = await adminAuth();
      
      const userRecord = await adminAuthInstance.getUserByEmail(email);
      return convertAdminUser(userRecord);
    } catch (error) {
      console.error('Error getting user by email:', error);
      return null;
    }
  }

  /**
   * Get all users (server-side only)
   */
  async getAllUsers(): Promise<User[]> {
    if (typeof window !== 'undefined') {
      throw new Error('getAllUsers can only be called from server-side');
    }

    if (isSampleDataMode()) {
      const { getAllSampleUsers } = await import('@/lib/sample-data/users');
      return getAllSampleUsers();
    }

    try {
      const { adminAuth } = await import('./admin');
      const adminAuthInstance = await adminAuth();
      
      // Get all users (limited to first 1000 for performance)
      const listUsersResult = await adminAuthInstance.listUsers(1000);
      
      return listUsersResult.users.map(userRecord => convertAdminUser(userRecord));
    } catch (error) {
      console.error('Error getting all users:', error);
      throw new Error('Failed to retrieve users from Firebase Auth');
    }
  }

  /**
   * Get users by role (server-side only)
   */
  async getUsersByRole(role: UserRoleType): Promise<User[]> {
    if (typeof window !== 'undefined') {
      throw new Error('getUsersByRole can only be called from server-side');
    }

    if (isSampleDataMode()) {
      const { getSampleUsersByRole } = await import('@/lib/sample-data/users');
      return getSampleUsersByRole(role);
    }

    try {
      const allUsers = await this.getAllUsers();
      return allUsers.filter(user => user.roles?.includes(role));
    } catch (error) {
      console.error('Error getting users by role:', error);
      return [];
    }
  }

  /**
   * Create a new user (server-side only)
   */
  async createUser(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    roles?: UserRoleType[];
    organizationId?: string;
    tenantId?: string;
    phoneNumber?: string;
    photoURL?: string;
  }): Promise<User> {
    if (typeof window !== 'undefined') {
      throw new Error('createUser can only be called from server-side');
    }

    if (isSampleDataMode()) {
      throw new Error('User creation not available in sample data mode');
    }

    try {
      const { adminAuth } = await import('./admin');
      const adminAuthInstance = await adminAuth();

      // Create user record
      const userRecord = await adminAuthInstance.createUser({
        email: userData.email,
        password: userData.password,
        displayName: `${userData.firstName} ${userData.lastName}`,
        phoneNumber: userData.phoneNumber,
        photoURL: userData.photoURL || '/user.png',
        emailVerified: false,
        disabled: false,
      });

      // Set custom claims for roles and organization
      const customClaims = {
        roles: userData.roles || ['Member'],
        firstName: userData.firstName,
        lastName: userData.lastName,
        organizationId: userData.organizationId || '',
        tenantId: userData.tenantId || userData.organizationId || '',
        createdBy: 'admin',
        createdAt: new Date().toISOString(),
      };

      await adminAuthInstance.setCustomUserClaims(userRecord.uid, customClaims);

      // Return the created user
      const updatedUserRecord = await adminAuthInstance.getUser(userRecord.uid);
      return convertAdminUser(updatedUserRecord);
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error('Failed to create user in Firebase Auth');
    }
  }

  /**
   * Update user (server-side only)
   */
  async updateUser(uid: string, updates: {
    email?: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    photoURL?: string;
    disabled?: boolean;
    roles?: UserRoleType[];
    organizationId?: string;
    tenantId?: string;
  }): Promise<User | null> {
    if (typeof window !== 'undefined') {
      throw new Error('updateUser can only be called from server-side');
    }

    if (isSampleDataMode()) {
      throw new Error('User updates not available in sample data mode');
    }

    try {
      const { adminAuth } = await import('./admin');
      const adminAuthInstance = await adminAuth();

      // Prepare user record updates
      const userRecordUpdates: {
        email?: string;
        displayName?: string;
        phoneNumber?: string;
        photoURL?: string;
        disabled?: boolean;
      } = {};
      
      if (updates.email) userRecordUpdates.email = updates.email;
      if (updates.displayName) userRecordUpdates.displayName = updates.displayName;
      if (updates.phoneNumber) userRecordUpdates.phoneNumber = updates.phoneNumber;
      if (updates.photoURL) userRecordUpdates.photoURL = updates.photoURL;
      if (typeof updates.disabled === 'boolean') userRecordUpdates.disabled = updates.disabled;

      // Update user record if there are changes
      if (Object.keys(userRecordUpdates).length > 0) {
        await adminAuthInstance.updateUser(uid, userRecordUpdates);
      }

      // Update custom claims if needed
      const customClaimUpdates: {
        firstName?: string;
        lastName?: string;
        roles?: UserRoleType[];
        organizationId?: string;
        tenantId?: string;
      } = {};
      if (updates.firstName) customClaimUpdates.firstName = updates.firstName;
      if (updates.lastName) customClaimUpdates.lastName = updates.lastName;
      if (updates.roles) customClaimUpdates.roles = updates.roles;
      if (updates.organizationId) customClaimUpdates.organizationId = updates.organizationId;
      if (updates.tenantId) customClaimUpdates.tenantId = updates.tenantId;

      if (Object.keys(customClaimUpdates).length > 0) {
        // Get existing custom claims first
        const existingUser = await adminAuthInstance.getUser(uid);
        const existingClaims = existingUser.customClaims || {};
        
        // Merge with updates
        const newClaims = { 
          ...existingClaims, 
          ...customClaimUpdates,
          updatedAt: new Date().toISOString()
        };
        
        await adminAuthInstance.setCustomUserClaims(uid, newClaims);
      }

      // Return updated user
      const updatedUserRecord = await adminAuthInstance.getUser(uid);
      return convertAdminUser(updatedUserRecord);
    } catch (error) {
      console.error('Error updating user:', error);
      return null;
    }
  }

  /**
   * Delete user (server-side only)
   */
  async deleteUser(uid: string): Promise<boolean> {
    if (typeof window !== 'undefined') {
      throw new Error('deleteUser can only be called from server-side');
    }

    if (isSampleDataMode()) {
      throw new Error('User deletion not available in sample data mode');
    }

    try {
      const { adminAuth } = await import('./admin');
      const adminAuthInstance = await adminAuth();
      
      await adminAuthInstance.deleteUser(uid);
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      return false;
    }
  }

  /**
   * Check if user has specific role
   */
  userHasRole(user: User, role: UserRoleType): boolean {
    return user.roles?.includes(role) || false;
  }

  // ===========================================================================
  // UNIFIED AUTHENTICATION METHODS
  // ===========================================================================

  private readonly STORAGE_KEYS = {
    PENDING_EMAIL: 'emailForSignIn',
    SENT_AT: 'signInLinkSentAt',
    AUTH_METHOD: 'lastAuthMethod'
  } as const;

  /**
   * Unified sign-in method that handles both password and magic link authentication
   */
  async signIn(credentials: AuthCredentials, options: AuthOptions): Promise<AuthResult> {
    try {
      const auth = getAuth();
      
      if (!auth) {
        return {
          success: false,
          error: {
            code: 'auth/configuration-error',
            i18nKey: 'auth.errors.configurationError',
            severity: 'error',
            category: 'system',
            retryable: false,
            userAction: 'contactSupport'
          }
        };
      }

      // Store auth method preference
      this.setAuthMethodPreference(options.method);

      switch (options.method) {
        case 'password':
          return await this.signInWithPassword(credentials);
        case 'magic-link':
          return await this.sendMagicLink(credentials, options.redirectUrl);
        default:
          throw new Error(`Unsupported auth method: ${options.method}`);
      }
    } catch (error) {
      const mappedError = mapFirebaseError(error as { code?: string });
      return { success: false, error: mappedError };
    }
  }

  /**
   * Complete magic link verification
   */
  async verifyMagicLink(url: string, email?: string): Promise<AuthResult> {
    try {
      const auth = getAuth();

      if (!isSignInWithEmailLink(auth, url)) {
        return {
          success: false,
          error: {
            code: 'auth/invalid-action-code',
            i18nKey: 'auth.errors.invalidActionCode',
            severity: 'error',
            category: 'auth',
            retryable: false,
            userAction: 'requestNew'
          }
        };
      }

      const userEmail = email || this.getPendingEmail();
      if (!userEmail) {
        return {
          success: false,
          error: {
            code: 'auth/missing-email',
            i18nKey: 'auth.errors.missingEmail',
            severity: 'error',
            category: 'validation',
            retryable: false,
            userAction: 'provideEmail'
          }
        };
      }

      const userCredential = await signInWithEmailLink(auth, userEmail, url);
      this.clearMagicLinkData();

      return {
        success: true,
        user: userCredential.user
      };
    } catch (error) {
      const mappedError = mapFirebaseError(error as { code?: string });
      return { success: false, error: mappedError };
    }
  }

  /**
   * Check if current URL is a magic link
   */
  isMagicLink(url: string): boolean {
    const auth = getAuth();
    return isSignInWithEmailLink(auth, url);
  }

  /**
   * Get pending magic link status
   */
  getPendingMagicLink(): MagicLinkStatus | null {
    if (typeof window === 'undefined') return null;

    const email = localStorage.getItem(this.STORAGE_KEYS.PENDING_EMAIL);
    const sentAt = localStorage.getItem(this.STORAGE_KEYS.SENT_AT);

    if (email && sentAt) {
      const expiresAt = new Date(new Date(sentAt).getTime() + (60 * 60 * 1000)).toISOString();
      return { email, sentAt, expiresAt };
    }

    return null;
  }

  /**
   * Resend magic link using stored email
   */
  async resendMagicLink(redirectUrl?: string): Promise<AuthResult> {
    const pending = this.getPendingMagicLink();
    if (!pending) {
      return {
        success: false,
        error: {
          code: 'auth/no-pending-request',
          i18nKey: 'auth.errors.noPendingRequest',
          severity: 'error',
          category: 'validation',
          retryable: false,
          userAction: 'startOver'
        }
      };
    }

    return this.sendMagicLink({ email: pending.email }, redirectUrl);
  }

  /**
   * Clear magic link data
   */
  clearMagicLinkData(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.STORAGE_KEYS.PENDING_EMAIL);
      localStorage.removeItem(this.STORAGE_KEYS.SENT_AT);
    }
  }

  /**
   * Get user's preferred authentication method
   */
  getAuthMethodPreference(): AuthMethod {
    if (typeof window === 'undefined') return 'password';
    return (localStorage.getItem(this.STORAGE_KEYS.AUTH_METHOD) as AuthMethod) || 'password';
  }

  /**
   * Set user's preferred authentication method
   */
  setAuthMethodPreference(method: AuthMethod): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEYS.AUTH_METHOD, method);
    }
  }

  // ===========================================================================
  // PRIVATE UNIFIED AUTH METHODS
  // ===========================================================================

  private async signInWithPassword(credentials: AuthCredentials): Promise<AuthResult> {
    if (!credentials.password) {
      return {
        success: false,
        error: {
          code: 'auth/missing-password',
          i18nKey: 'auth.errors.missingPassword',
          severity: 'error',
          category: 'validation',
          retryable: false,
          userAction: 'providePassword'
        }
      };
    }

    const auth = getAuth();
    const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
    
    return {
      success: true,
      user: userCredential.user
    };
  }

  private async sendMagicLink(credentials: AuthCredentials, redirectUrl?: string): Promise<AuthResult> {
    const auth = getAuth();
    
    const actionCodeSettings: ActionCodeSettings = {
      url: redirectUrl || `${window.location.origin}/auth/verify-magic-link`,
      handleCodeInApp: true,
      iOS: {
        bundleId: 'app.courtly.ios'
      },
      android: {
        packageName: 'app.courtly.android',
        installApp: true,
        minimumVersion: '12'
      }
    };

    await sendSignInLinkToEmail(auth, credentials.email, actionCodeSettings);

    if (typeof window !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEYS.PENDING_EMAIL, credentials.email);
      localStorage.setItem(this.STORAGE_KEYS.SENT_AT, new Date().toISOString());
    }

    return {
      success: true,
      requiresVerification: true
    };
  }

  private getPendingEmail(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this.STORAGE_KEYS.PENDING_EMAIL);
  }

  private clearAllStorageData(): void {
    if (typeof window !== 'undefined') {
      Object.values(this.STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    }
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if user has required role
 */
export function hasRole(user: User, role: UserRoleType): boolean {
  return user.roles?.includes(role) || false;
}

/**
 * Check if user is Super Admin
 */
export function isSuperAdmin(user: User): boolean {
  return hasRole(user, 'Super Admin') || user.customClaims?.isSuperAdmin === true;
}

/**
 * Check if user is Admin
 */
export function isAdmin(user: User): boolean {
  return hasRole(user, 'Admin') || user.customClaims?.isAdmin === true;
}

/**
 * Extract user roles from custom claims
 */
export function extractUserRoles(customClaims: Record<string, unknown> | null | undefined): UserRoleType[] {
  const roles = customClaims?.roles;
  if (Array.isArray(roles)) {
    return roles as UserRoleType[];
  }
  return ['Member'];
}

/**
 * Check if user is in sample data mode
 */
export function isInSampleDataMode(): boolean {
  return isSampleDataMode();
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const authService = FirebaseAuthService.getInstance(); 
